import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import Grid from '@material-ui/core/Grid';
import RoleForm from '../../components/RoleForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Page from '@afosto/app-components-react/Page';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { rbac as rbacApi } from '@afosto/iam/api/Rbac';
import translations from './translations';
import { scopesFormatter } from './formatters';

/**
 * Fetch all the roles defined by the user
 * @returns {Promise}
 */
const fetchRoles = async () => {
  try {
    const response = await rbacApi.listRoles();
    const { data = [] } = response;

    const formattedRoles = data.reduce(
      (acc, roleData) => [
        ...acc,
        {
          id: roleData.id,
          name: roleData.name,
          scopes: roleData.scopes,
        },
      ],
      [],
    );

    return sortArrayByProperty(formattedRoles, 'name');
  } catch (err) {
    return [];
  }
};

const Rbac = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:roles:read']);

  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [role, setRole] = useState(null);
  const [isFetchingRole, setIsFetchingRole] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const confirm = useConfirm();

  const dataSource = useDataGrid(fetchRoles, {
    modules: ['dataProperties', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.rbac.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'scopes',
          name: intl.formatMessage({
            id: 'dataGrid.columns.rbac.scopes',
            defaultMessage: 'Scopes',
            description: 'The column name for column scopes',
          }),
          type: 'scope',
        },
      ],
    },
  });

  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  /**
   * Creates a new rbac role for the tenant
   * @param roleData {Object}
   * @returns {Promise}
   */
  const handleCreateRole = async roleData => {
    try {
      const { name, scopes } = roleData;
      const response = await rbacApi.createRole({
        name,
        scopes: scopes.map(scope => scope.label),
      });
      const { data = {} } = response;
      dataSource.reloadDataSource();
      setShowCreateRole(false);
      enqueueSnackbar(intl.formatMessage(translations.feedback.roleCreated), {
        variant: 'success',
      });

      return data;
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.roleNotCreated), {
        variant: 'error',
      });
      return Promise.reject(error);
    }
  };

  /**
   * Save the edited role for the tenant
   * @param roleData {Object}
   * @returns {Promise}
   */
  const handleSaveRole = async roleData => {
    try {
      const { name, scopes } = roleData;
      const response = await rbacApi.updateRole(role.id, {
        name,
        scopes: scopes.map(scope => scope.label),
      });
      const { data } = response;

      dataSource.reloadDataSource();
      setRole(data);
      setShowEditRole(false);
      enqueueSnackbar(intl.formatMessage(translations.feedback.roleSaved), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.roleNotSaved), {
        variant: 'error',
      });
    }
  };

  /**
   * Delete the tenants role by its ID
   * @param id {Number}
   * @returns {Promise}
   */
  const handleDeleteRole = id => async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.page.rbac.delete.title),
        description: intl.formatMessage(translations.page.rbac.delete.text),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.page.rbac.delete.actions.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.page.rbac.delete.actions.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        await rbacApi.deleteRole(id);

        dataSource.reloadDataSource();
        dataSource.activeItem.setActiveItem(null);
        setRole(null);
        enqueueSnackbar(intl.formatMessage(translations.feedback.roleDeleted), {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.roleNotDeleted), {
        variant: 'error',
      });
    }
  };

  /**
   * Fetch the role data if a role row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;

    if (activeItem) {
      /**
       * Fetch a roles data by its id
       * @param {String} id
       * @returns {Promise}
       */
      const fetchRole = async id => {
        setIsFetchingRole(true);

        try {
          const response = await rbacApi.getRole(id);
          const { data = {} } = response;

          if (isMounted) {
            setRole(data);
          }
        } catch (error) {
          if (isMounted) {
            setRole(null);
          }
        } finally {
          if (isMounted) {
            setIsFetchingRole(false);
          }
        }
      };

      fetchRole(activeItem);
    } else {
      setRole(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const previewMenu = [
    ...(role && hasPermissions(['iam:roles:write'])
      ? [
          {
            key: 'role_edit',
            translationKey: 'actions.edit',
            label: 'Edit',
            icon: 'pencil',
            shortcut: true,
            onClick: () => setShowEditRole(true),
          },
          {
            key: 'role_delete',
            translationKey: 'app.role.preview.delete',
            label: 'Delete',
            icon: 'trash-alt',
            onClick: handleDeleteRole(role.id),
            color: 'secondary',
          },
        ]
      : []),
  ];

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.page.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.page.title)}
        style={{ flex: 1 }}
        fullWidth
        primaryAction={
          hasPermissions(['iam:roles:write'])
            ? {
                text: intl.formatMessage(translations.page.rbac.create.button),
                action: () => {
                  setShowCreateRole(true);
                },
              }
            : null
        }
      >
        <DataGrid dataSource={dataSource} formatters={{ scope: scopesFormatter }} />
      </Page>
      <DataGridPreview
        title={role ? role.name : null}
        menu={previewMenu}
        open={dataSource.activeItem.activeItem !== null}
        onClose={() => dataSource.activeItem.setActiveItem(null)}
      >
        <LoadingWrapper isLoading={isFetchingRole}>
          {role && <RoleForm inline role={role} onSubmit={handleSaveRole} />}
        </LoadingWrapper>
      </DataGridPreview>
      <FormDrawer
        title={intl.formatMessage(translations.page.rbac.create.title)}
        isOpen={showCreateRole}
        onClose={() => setShowCreateRole(false)}
        drawerWidth="400px"
      >
        <RoleForm onSubmit={handleCreateRole} onCancel={() => setShowCreateRole(false)} />
      </FormDrawer>
      <FormDrawer
        title={intl.formatMessage(translations.page.rbac.edit.title)}
        isOpen={showEditRole}
        onClose={() => setShowEditRole(false)}
        drawerWidth="400px"
      >
        <RoleForm onSubmit={handleSaveRole} onCancel={() => setShowEditRole(false)} role={role} />
      </FormDrawer>
    </Grid>
  );
};

export default Rbac;
